import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Button} from '@/components/ui/button'
import {Clock, CheckCircle, XCircle, Play, Archive, RefreshCw, Cog} from 'lucide-react'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {PageLayout} from '@/components/shared/PageLayout'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {Icon} from '@/components/shared/Icon'
import {StatValue} from '@/components/shared/StatValue'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {CONCEPT_ICON} from '@/lib/concepts'

interface ProvisioningStats {
    queued: number
    running: number
    failed: number
    done: number
    total: number
}

export function ProvisioningDashboard() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.provisioning.dashboardPageTitle)
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.provisioning.manage)

    const {data: stats, isLoading, error} = useQuery({
        queryKey: ['provisioning-stats'],
        queryFn: () => services.provisioning.stats() as Promise<ProvisioningStats>,
        refetchInterval: 5000, // Refresh every 5 seconds
    })

    const processAllMutation = useEntityMutation({
        mutationFn: () => services.provisioning.processAll(),
        invalidate: [['provisioning-stats'], ['provisioning-tasks']],
        successMessage: i18n.provisioning.tasksProcessed,
    })

    const requeueMutation = useEntityMutation({
        mutationFn: () => services.provisioning.requeueReady(),
        invalidate: [['provisioning-stats'], ['provisioning-tasks']],
        successMessage: i18n.provisioning.tasksRequeued,
    })

    const archiveMutation = useEntityMutation({
        mutationFn: () => services.provisioning.archive(),
        invalidate: [['provisioning-stats'], ['provisioning-tasks']],
        successMessage: i18n.provisioning.tasksArchived,
    })

    return (
        <PageLayout
            data-testid="provisioning-dashboard-page"
            variant="dashboard"
            icon={CONCEPT_ICON.dashboard}
            title={i18n.provisioning.provisioningDashboard}
            description={i18n.provisioning.provisioningDashboardDesc}
            loading={isLoading}
            error={error}
            actions={canManage ? (
                <InlineRow>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => processAllMutation.mutate()}
                        disabled={processAllMutation.isPending}
                        data-testid="btn-process-all"
                    >
                        <Icon icon={Cog} className="mr-2"/>
                        {processAllMutation.isPending ? i18n.provisioning.processingAll : i18n.provisioning.processAll}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => requeueMutation.mutate()}
                        disabled={requeueMutation.isPending}
                        data-testid="btn-requeue-ready"
                    >
                        <Icon icon={RefreshCw} className="mr-2"/>
                        {requeueMutation.isPending ? i18n.provisioning.requeueing : i18n.provisioning.requeueReady}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => archiveMutation.mutate()}
                        disabled={archiveMutation.isPending}
                        data-testid="btn-archive-completed"
                    >
                        <Icon icon={Archive} className="mr-2"/>
                        {archiveMutation.isPending ? i18n.provisioning.archiving : i18n.provisioning.archiveCompleted}
                    </Button>
                </InlineRow>
            ) : undefined}
        >
            <DetailGrid columns={4}>
                <StatCard
                    title={i18n.provisioning.queued as string}
                    description={i18n.provisioning.queuedDesc as string}
                    value={stats?.queued}
                    icon={<Icon icon={Clock} muted/>}
                    isLoading={isLoading}
                    data-testid="stat-queued"
                />
                <StatCard
                    title={i18n.provisioning.running as string}
                    description={i18n.provisioning.runningDesc as string}
                    value={stats?.running}
                    icon={<Icon icon={Play} className="text-status-info"/>}
                    isLoading={isLoading}
                    data-testid="stat-running"
                />
                <StatCard
                    title={i18n.provisioning.failed as string}
                    description={i18n.provisioning.failedDesc as string}
                    value={stats?.failed}
                    icon={<Icon icon={XCircle} className="text-destructive"/>}
                    isLoading={isLoading}
                    highlight={Boolean(stats?.failed && stats.failed > 0)}
                    data-testid="stat-failed"
                />
                <StatCard
                    title={i18n.provisioning.completed as string}
                    description={i18n.provisioning.completedDesc as string}
                    value={stats?.done}
                    icon={<Icon icon={CheckCircle} className="text-status-success"/>}
                    isLoading={isLoading}
                    data-testid="stat-completed"
                />
            </DetailGrid>

            <Card>
                <CardHeader>
                    <CardTitle data-testid="card-title-about-provisioning-tasks">{i18n.provisioning.aboutProvisioningTasks}</CardTitle>
                    <CardDescription>
                        {i18n.provisioning.aboutDesc}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <DetailGrid columns={2}>
                        <Stack gap="sm">
                            <Strong>{i18n.provisioning.taskLifecycle}</Strong>
                            <Stack gap="xs">
                                <MutedText block><Strong>{i18n.provisioning.taskLifecycleQueued}</Strong> - {i18n.provisioning.taskLifecycleQueuedDesc}</MutedText>
                                <MutedText block><Strong>{i18n.provisioning.taskLifecycleRunning}</Strong> - {i18n.provisioning.taskLifecycleRunningDesc}</MutedText>
                                <MutedText block><Strong>{i18n.provisioning.taskLifecycleFailed}</Strong> - {i18n.provisioning.taskLifecycleFailedDesc}</MutedText>
                                <MutedText block><Strong>{i18n.provisioning.taskLifecycleDone}</Strong> - {i18n.provisioning.taskLifecycleDoneDesc}</MutedText>
                            </Stack>
                        </Stack>
                        <Stack gap="sm">
                            <Strong>{i18n.provisioning.actionsTitle}</Strong>
                            <Stack gap="xs">
                                <MutedText block><Strong>{i18n.provisioning.actionGrant}</Strong> - {i18n.provisioning.actionGrantDesc}</MutedText>
                                <MutedText block><Strong>{i18n.provisioning.actionRevoke}</Strong> - {i18n.provisioning.actionRevokeDesc}</MutedText>
                                <MutedText block><Strong>{i18n.provisioning.actionUpdateAccount}</Strong> - {i18n.provisioning.actionUpdateAccountDesc}</MutedText>
                            </Stack>
                        </Stack>
                    </DetailGrid>
                </CardContent>
            </Card>
        </PageLayout>
    )
}

interface StatCardProps {
    title: string
    description: string
    value: number | undefined
    icon: React.ReactNode
    isLoading: boolean
    highlight?: boolean
    'data-testid'?: string
}

function StatCard({title, description, value, icon, isLoading, highlight, 'data-testid': testId}: StatCardProps) {
    return (
        <Card className={highlight ? 'border-destructive' : ''} data-testid={testId}>
            <RowCardHeader className="pb-2">
                <CardTitle className="font-medium" data-testid="card-title-stat">{title}</CardTitle>
                {icon}
            </RowCardHeader>
            <CardContent>
                <StatValue value={isLoading ? '...' : (value ?? 0)} data-testid={testId ? `${testId}-value` : undefined}/>
                <MutedText size="xs">{description}</MutedText>
            </CardContent>
        </Card>
    )
}
